"use client";

import { useTranslations } from "next-intl";
import { GlassCard } from "@/components/glass-card";
import { TrendingUp } from "lucide-react";

interface FinancialSummaryProps {
  totalIncome?: number | null;
  fundraisingIncome?: number | null;
  totalExpenses?: number | null;
}

function formatCurrency(value: number): string {
  return value.toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

export function FinancialSummary({
  totalIncome,
  fundraisingIncome,
  totalExpenses,
}: FinancialSummaryProps) {
  const t = useTranslations("projects");

  if (!totalIncome && !fundraisingIncome && !totalExpenses) {
    return null;
  }

  const stats = [
    { label: t("financialSummary.totalIncome"), value: totalIncome },
    { label: t("financialSummary.fundraisingIncome"), value: fundraisingIncome },
    { label: t("financialSummary.totalExpenses"), value: totalExpenses },
  ].filter((s) => s.value != null && s.value > 0);

  if (stats.length === 0) return null;

  return (
    <GlassCard>
      <h3 className="flex items-center gap-2 text-base font-semibold mb-3">
        <TrendingUp className="h-4 w-4 text-green-600" />
        {t("financialSummary.title")}
      </h3>
      <div className="grid grid-cols-1 gap-3">
        {stats.map(({ label, value }) => (
          <div key={label} className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">{label}</span>
            <span className="text-sm font-semibold">
              {formatCurrency(value!)}
            </span>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-3">
        {t("financialSummary.fiscalYearNote")}
      </p>
    </GlassCard>
  );
}
